import { FunctionalComponent } from 'preact';
import { stylesheet } from 'astroturf';
import { useState, useEffect, useCallback } from 'preact/hooks';
import { logger } from '@accedo/logger';
import {
  NavScroll,
  OnScrollOpt,
  VERTICAL,
  emitScrollEvent,
} from '@accedo/virtual-scroll';
import { Nav } from '@accedo/spatial-nav';
import clsx from 'clsx';
import { useErrorScreen } from '##/components/Error';
import tenApi, { RequestRejectionError } from '##/services/tenApi';
import { useInterval, useReferredState, useKeyPress } from '##/utils/hooks';
import {
  findChannel,
  safeAssignFocus,
  LIVE_ROW_ID_PREFIX,
  onBackPressOnPagesWithSideNav,
} from '##/utils';
import {
  LiveShowForOverlay,
  LiveV2Response,
} from '##/services/tenApi/collections/types';
import { nav } from '##/init';
import { config, focusCache } from '##/services/store/store';
import { setLoadingIndicatorVisibility } from '##/components/LoadingIndicator/LoadingIndicator';
import { scrollThenFocus } from '##/utils/scroll';
import {
  TealiumErrorCode,
  TealiumErrorContext,
} from '##/services/analytics/Tealium/TealiumErrorTypes';
import LiveSchedulePageBackground from '##/assets/live-schedule-background.svg';
import { SecondaryNav } from '../SecondaryNav/SecondaryNav';
import { PoweredByBranding } from '../PoweredByBranding';
import { LiveTVOverlay } from './LiveTvOverlay';
import { LiveTvRow } from './LiveTvRow';

const LIVE_PAGE_SECONDARY_NAV_ID = 'live-page-secondary-nav-id';
const LIVE_PAGE_LAYOUT_NAV = 'livepage-layout';

export const FIRST_GROUP_HEADING_TOP_MARGIN = 79;
export const GROUP_HEADING_FONT_SIZE = 18;
export const GROUP_HEADING_VERTICAL_MARGIN = 21;

const SCROLL_END_BOUNDARY = 3;

const LIVE_TV_TEXT = 'LIVE TV';

const styles = stylesheet`
  @import '##/styles/import.scss';

  .live-page-container {
      width: 100vw;
      height: 100vh;
      font-family: barlow;
      display: flex;
  };

  .live-page-no-groups {
    justify-content: center;
  }

  .flex-column {
    display: flex;
    flex-direction: column;
  }

  .index-bg-container {
      position: absolute;
      top: 0;
      z-index: -1;

      & svg {
        height: 100vh;
        width: 100vw;
      }
  }

  .live-schedule-header {
    margin-bottom: pxToVw(${GROUP_HEADING_VERTICAL_MARGIN});
    display: flex;
    flex-direction: column;
    text-transform: capitalize;
    margin-left: pxToVw(34);
    margin-top: pxToVw(${GROUP_HEADING_VERTICAL_MARGIN});
    font-size: pxToVw(${GROUP_HEADING_FONT_SIZE});
  }

  .live-schedule-header-first {
    @extend .live-schedule-header;

    margin-top: pxToVw(${FIRST_GROUP_HEADING_TOP_MARGIN});
  }

  .full-carousel{
    overflow: hidden;
  }

  .live-secondary-nav {
    padding-top: pxToVh(39);
    position: relative;

    & .live-secondary-nav-title {
      padding-bottom: pxToVh(36);
    }
  }

  .powered-by-logo {
    height: pxToVh(23);
    top: 6%;
    left: 9%;
    max-width: pxToVw(186);
  }

  .live-content {
    display:flex;
    flex-direction:row;
  }

  .no-shrink {
    flex-shrink: 0;
    height: 100vw;
  }

`;

const livePagelazyLoadBoundary = 3;

export const LiveTvPage: FunctionalComponent<{}> = () => {
  const configItems = config.$get();
  const { region } = configItems.geo;
  const [liveResponse, setLiveResponse] = useState<LiveV2Response>();
  const [showSecondaryNav, setShowSecondaryNav] = useState(true);
  const [activeGenre, activeGenreRef, setActiveGenre] = useReferredState(
    'featured',
  );
  const [currentTime, setCurrentTime] = useState(+new Date());
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { showError, sendErrorToTealium } = useErrorScreen();
  const [hoverFocus, setHoverFocus] = useState<number | string | null>(null);
  const [showLiveOverlay, setShowLiveOverlay] = useState<boolean>(false);
  const [liveShow, setLiveShow] = useState<LiveShowForOverlay | null>(null);

  const liveShowsEndpoint = configItems?.app?.liveTvEndpoint;

  const getLiveData = async (): Promise<void> => {
    if (!liveShowsEndpoint) return;
    let liveDataResponse: LiveV2Response | null = null;

    try {
      liveDataResponse = await tenApi.collections.getLiveChannels(4);

      setLiveResponse(liveDataResponse);

      const { channels, groups = [] } = liveDataResponse || {};

      const firstGroup = groups?.[0]?.name?.toLowerCase();

      if (firstGroup !== activeGenreRef.current && groups?.length > 1) {
        setActiveGenre(firstGroup);
      }

      setIsLoading(false);
      setLoadingIndicatorVisibility?.(false);
      const appUrl = new URL(window.location.href);
      const channelId = appUrl.searchParams.get('channel');

      if (channelId) {
        const channelMatch = findChannel(channels, channelId as string);

        focusCache.livePage.$set({
          lastFocusChannelId: channelMatch?.key,
        });
      }
    } catch (err) {
      logger.error(err);
      sendErrorToTealium({
        errorCode: TealiumErrorCode.FetchLiveTv,
        errorContext: TealiumErrorContext.API,
        errorDetail: JSON.stringify(err),
        errorSource: `${liveShowsEndpoint}/${region}`,
        errorMessage: '',
      });
      setIsLoading(false);
      setLoadingIndicatorVisibility?.(false);
      showError(
        (err as RequestRejectionError).internalErrorType || 'NETWORK_ERROR',
      );
    }
  };

  const { groups, channels, images } = liveResponse || {};

  useInterval(async () => {
    if (!liveShowsEndpoint) return;
    try {
      const liveNewResponse = await tenApi.collections.getLiveChannels(4);

      const newResponseStartTime = liveNewResponse?.channels?.map(
        (item) => item.liveShow?.startTime,
      );
      const currentLiveStartTime = channels?.map(
        (item) => item.liveShow?.startTime,
      );

      const compareTimeDifference = (
        currentData: number[],
        newData: number[],
      ): boolean =>
        currentData.length === newData.length &&
        newData.every(
          (element, index: number) => element === currentData[index],
        );

      const responseIsNotNewData = compareTimeDifference(
        newResponseStartTime as number[],
        currentLiveStartTime as number[],
      );

      if (!responseIsNotNewData) {
        setLiveResponse(liveNewResponse);
      }
    } catch (err) {
      logger.error(err);
      showError(
        (err as RequestRejectionError).internalErrorType || 'NETWORK_ERROR',
      );
    }
    setCurrentTime(+new Date());
  }, 60000);

  useEffect(() => {
    getLiveData();
  }, []);

  useEffect(() => {
    if (channels?.length) {
      const {
        lastFocusChannelId,
        lastChannelIdx,
        isComingUp,
        Later,
      } = focusCache.livePage.$get();

      const matchingResultIdx =
        lastChannelIdx ||
        channels.findIndex((result) => result.key === lastFocusChannelId);

      if (matchingResultIdx > -1) {
        let lastFocusId = '';
        if (isComingUp && !Later) {
          lastFocusId = `next-tile-${matchingResultIdx}`;
        } else if (Later && !isComingUp) {
          lastFocusId = `later-tile-${matchingResultIdx}`;
        } else {
          lastFocusId = `live-now-tile-${matchingResultIdx}`;
        }
        setHoverFocus(matchingResultIdx);

        if (matchingResultIdx >= livePagelazyLoadBoundary) {
          scrollThenFocus(LIVE_PAGE_LAYOUT_NAV, matchingResultIdx, lastFocusId);
        } else {
          nav.assignFocus(lastFocusId);
        }
      } else {
        safeAssignFocus(LIVE_PAGE_SECONDARY_NAV_ID, LIVE_PAGE_LAYOUT_NAV);
      }
      focusCache.livePage.$clear();
    }
  }, [channels?.length]);

  const onSecondaryNavSelect = useCallback(
    (genreId: string, secondaryNavItemId: string): void => {
      setActiveGenre(genreId?.toLowerCase());

      const firstIndex = channels?.findIndex(
        (item) => item.genre?.toLowerCase() === genreId,
      );

      emitScrollEvent('scroll-to-index', firstIndex, LIVE_PAGE_LAYOUT_NAV);

      nav.setActiveChild(
        LIVE_PAGE_LAYOUT_NAV,
        `${LIVE_ROW_ID_PREFIX}-${firstIndex}`,
      );

      /*
      TODO: Re-enable post phase 1 launch

      const channelGroupIndex = parseInt(secondaryNavItemId?.slice(-1), 10);

      TealiumBridge.onActiveGenreChange({
        channelGroup: genreId,
        numberOfChannelGroups: groups?.length || 0,
        channelGroupIndex,
      });
      */

      setTimeout(() => {
        safeAssignFocus(secondaryNavItemId);
      }, 100);
    },
    [Boolean(channels?.length), Boolean(groups?.length)],
  );

  const onScroll = useCallback(
    (scrollProps: OnScrollOpt) => {
      const { index } = scrollProps;
      const { genre } = channels?.[index] || {};
      const { length } = groups || {};

      if (!length || length <= 1) {
        return;
      }

      if (genre?.toLowerCase() !== activeGenreRef.current) {
        // change active genre when user navigates up/down to a channel of a different genre
        const genreIdx = groups?.findIndex(
          (group) => group?.name?.toLowerCase() === genre?.toLowerCase(),
        );

        const nextActiveGenre = `${LIVE_PAGE_SECONDARY_NAV_ID}-item-${genreIdx}`;

        nav.setActiveChild(LIVE_PAGE_SECONDARY_NAV_ID, nextActiveGenre);

        setActiveGenre(genre?.toLowerCase() || '');
      }
    },
    [Boolean(channels?.length)],
  );

  useKeyPress('BACK', (): boolean => {
    const { parent } = nav?.currentFocusNode || {};

    if (parent?.includes(LIVE_ROW_ID_PREFIX)) {
      const { activeChild } = nav.getNode(LIVE_PAGE_SECONDARY_NAV_ID) || {};

      if(activeChild) {
        safeAssignFocus(activeChild);
      }

      return true;
    }

    return onBackPressOnPagesWithSideNav(parent || '', LIVE_PAGE_SECONDARY_NAV_ID);
  });

  const numOfGroups = groups?.length || 0;
  const hasNoGroups = !numOfGroups || numOfGroups <= 1;
  const lastGroupItems = groups?.[numOfGroups - 1]?.items || [];

  const { brandingImageDarkUrl } = images || {};
  const { liveTvSettings } = config.$get().app || {};

  const liveBrandingImage =
    brandingImageDarkUrl || liveTvSettings?.brandingImageDarkUrl || '';

  return (
    <div
      className={clsx(
        styles.livePageContainer,
        hasNoGroups && styles.livePageNoGroups,
      )}
    >
      <div className={styles.indexBgContainer}>
        <LiveSchedulePageBackground />
      </div>

      {isLoading ? (
        <></>
      ) : (
        <Nav.Horizontal id="live-tv-index-page-nav">
          <div className={styles.noShrink}>
            {!hasNoGroups && (
              <SecondaryNav
                className={styles.liveSecondaryNav}
                navTitleClassName={
                  liveBrandingImage ? styles.liveSecondaryNavTitle : ''
                }
                items={
                  groups?.map((group) => {
                    const { name } = group || {};

                    return {
                      text: `${name?.[0]?.toUpperCase()}${name?.slice(1)}`,
                      key: name?.toLowerCase(),
                    };
                  }) || []
                }
                title={LIVE_TV_TEXT}
                navId={LIVE_PAGE_SECONDARY_NAV_ID}
                setShowing={(active) => {
                  setShowSecondaryNav(active);
                }}
                hidden={!showSecondaryNav}
                onSelect={onSecondaryNavSelect}
                activeKey={activeGenre}
              >
                {Boolean(liveBrandingImage) && (
                  <PoweredByBranding
                    logoUrl={liveBrandingImage}
                    logoClassName={styles.poweredByLogo}
                  />
                )}
              </SecondaryNav>
            )}
          </div>
          {showLiveOverlay && liveShow && channels && (
            <LiveTVOverlay
              onDismiss={() => {
                safeAssignFocus(
                  focusCache.livePage.$get().lastFocusedElementId,
                );
                focusCache.livePage.$clear();
              }}
              liveShow={liveShow}
              setShowOverlay={setShowLiveOverlay}
            />
          )}

          <div className={styles.flexColumn}>
            <NavScroll
              longScrollThresholdPx={64}
              animate={false}
              className={styles.fullCarousel}
              id="livepage-layout"
              orientation={VERTICAL}
              scrollEndBoundary={
                lastGroupItems.length <= SCROLL_END_BOUNDARY
                  ? SCROLL_END_BOUNDARY - 1
                  : SCROLL_END_BOUNDARY
              }
              lazyLoadBoundary={livePagelazyLoadBoundary}
              navProps={{ isIndexAlign: true }}
              primary
              onScroll={onScroll}
            >
              {groups?.map(({ name, items }, groupIdx) => (
                <>
                  {Boolean(name) && (
                    <div
                      className={
                        !groupIdx
                          ? styles.liveScheduleHeaderFirst
                          : styles.liveScheduleHeader
                      }
                    >
                      {numOfGroups > 1 ? name : LIVE_TV_TEXT}
                    </div>
                  )}
                  {items?.map((channelItem) => (
                    <LiveTvRow
                      firstGroupHeadingHeight={
                        FIRST_GROUP_HEADING_TOP_MARGIN +
                        GROUP_HEADING_FONT_SIZE +
                        GROUP_HEADING_VERTICAL_MARGIN
                      }
                      liveItem={channelItem}
                      index={channelItem?.channelIdx || 0}
                      currentTime={currentTime}
                      setHoverFocus={setHoverFocus}
                      hoverFocus={hoverFocus}
                      setLiveShow={setLiveShow}
                      setShowLiveOverlay={setShowLiveOverlay}
                    />
                  ))}
                </>
              ))}
            </NavScroll>
          </div>
        </Nav.Horizontal>
      )}
    </div>
  );
};
